import { Injectable, inject } from '@angular/core';

import { getSourceEventId } from '@frontend/sports/betting-offer/feature/model';
import { ApiService } from '@frontend/sports/common/api-utils';
import { RacingConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { toDictionary } from '@frontend/sports/common/core/utils/collection';
import { BogVersion } from '@frontend/sports/types/configuration/client-configuration';
import { EventsService, NativeEventType } from '@frontend/vanilla/core';
import { isEqual } from 'lodash-es';
import { EMPTY, Observable, Subject, catchError, distinctUntilChanged, filter, map, merge, of, shareReplay, switchMap, takeUntil, timer } from 'rxjs';

import { BestOddsGuaranteedToken } from '../../../betslip/modules/reward-tokens/reward-tokens.model';
import { PayoutType, RewardTokenType } from '../../tokens-base/token-base.models';
import { BogToken, BogTokensResponse } from '../models/models';
import { GlobalBestOddsGuaranteeAvailabilityService } from './global-best-odds-guarantee-availability.service';

@Injectable({ providedIn: 'root' })
export class BogTokenProviderService {
    private racingConfig = inject(RacingConfig);
    private apiService = inject(ApiService);
    private bogAvailabilityService = inject(GlobalBestOddsGuaranteeAvailabilityService);
    private loggerFactory = inject(LoggerFactory);
    private eventsService = inject(EventsService);

    private readonly logger: SportsRemoteLogger;

    private readonly refreshSubject = new Subject<void>();
    readonly tokens$: Observable<BestOddsGuaranteedToken[]> = this.isTokenBasedBogEnabled() ? this.poolTokens() : EMPTY;

    constructor() {
        this.logger = this.loggerFactory.getLogger('BogTokenProviderService');
    }

    refresh(): void {
        this.refreshSubject.next();
    }

    private getBogTokens(): Observable<BogTokensResponse> {
        return this.apiService.get<BogTokensResponse>('crmOffers/GetBogTokens').pipe(
            catchError((error) => {
                this.logger.error(error, 'failed to get bog tokens');

                return [];
            }),
        );
    }

    private poolTokens(): Observable<BestOddsGuaranteedToken[]> {
        return merge(
            of([]),
            this.bogAvailabilityService.isBogAvailable$.pipe(
                switchMap((isBogAvailable) => {
                    if (!isBogAvailable) {
                        return of([]);
                    }

                    const resetTerminal$ = this.eventsService.events.pipe(filter((event) => event?.eventName === NativeEventType.RESET_TERMINAL));

                    return merge(this.refreshSubject.asObservable(), timer(0, this.racingConfig.refreshInterval)).pipe(
                        switchMap(() => this.getBogTokens()),
                        map((response) => response.bogTokens.map(this.mapToRewardToken)),
                        takeUntil(resetTerminal$),
                    );
                }),
            ),
        ).pipe(
            distinctUntilChanged((prev, current) => isEqual(prev, current)),
            shareReplay(1),
        );
    }

    private mapToRewardToken(token: BogToken): BestOddsGuaranteedToken {
        return {
            id: token.id.toString(),
            cmsItemId: '',
            startDate: new Date(token.schedule.fromUtc),
            expiryDate: new Date(token.schedule.toUtc),
            rewardTokenType: RewardTokenType.BestOddsGuaranteed,
            userTokenId: token.id.toString(),
            tokenType: '',
            maximumStake: token.stakeCap?.value,
            payoutType: PayoutType.Cash,
            filter: {
                sports: [],
                meetings: [],
                competitions: [],
                fixtures: [],
                marketTemplates: [],
                marketParameters: [],
                markets: [],
                options: [],
                slipTypes: token.slipTypes,
                schedule: {
                    daysOfTheWeek: token.schedule.daysOfWeek,
                    timesOfTheDay: token.schedule.timesOfDay.map((timeOfDay) => ({ ...timeOfDay })),
                },
                sportsFilter: toDictionary(
                    token.sports,
                    (sportConfig) => sportConfig.id,
                    (sportConfig) => ({
                        competitions: sportConfig.competitionIds.map((id) => +getSourceEventId(id)),
                        regions: sportConfig.regionIds,
                        meetings: sportConfig.meetingIds.map((id) => +getSourceEventId(id)),
                    }),
                ),
            },
            version: '',
        };
    }

    private isTokenBasedBogEnabled() {
        return this.racingConfig.isBOGEnabled && this.racingConfig.bogVersion === BogVersion.TokenBased;
    }
}
